import { buildClarityWorkspaceUrl } from './build-clarity-workspace-url.util';
import {
	readClarityWorkspaceRoute,
	toClaritySectionUrl,
	toNavigationUnlessCurrent,
} from './clarity-workspace-route.util';
import { CLARITY_DOCUMENTS_ROUTE_SEGMENT } from './constants/clarity-documents-route.constant';
import type { ClarityDocumentTarget } from './models/clarity-document-target.model';
import type { ClarityWorkspaceNavigation } from './models/clarity-workspace-navigation.model';
import { parseClarityWorkspaceLocation } from './parse-clarity-workspace-location.util';

/** Ported from Clarity `apps/web/src/shared/hooks/useWorkspaceRoute.ts` (commit `63bab2253`). */
/**
 * Opening a document — brain's `openDoc(fileId, { page, replace })`: a **push** unless the target's
 * `replace` is set, to the current domain's `documents` tab with `doc` set to `fileId` and `page` set from
 * the target, that drops `file`, `oi` and `action`, carries every other query key verbatim, and is `null` —
 * no navigation — when that URL is `currentUrl` (their TR-014).
 *
 * `page` without a `doc` is meaningless and `file` wins over `doc`; both rules live in
 * {@link buildClarityWorkspaceUrl}, which is why neither is repeated here.
 *
 * Only the integration is adapted: the current URL is an argument read at call time, the answer sits
 * under `sectionPath`, the push-or-replace choice travels inside the target — brain's own third argument,
 * kept to three parameters — and the navigation is handed back rather than performed.
 */
export function toClarityDocumentNavigation(
	currentUrl: string,
	sectionPath: string,
	target: ClarityDocumentTarget,
): ClarityWorkspaceNavigation | null {
	const current = readClarityWorkspaceRoute(currentUrl, sectionPath);
	const { domainId } = parseClarityWorkspaceLocation(current.params, current.search);
	const url = buildClarityWorkspaceUrl(
		{
			domainId,
			tab: CLARITY_DOCUMENTS_ROUTE_SEGMENT,
			doc: target.fileId,
			page: target.page ?? null,
			file: null,
			oi: null,
			action: null,
		},
		current.search,
	);

	return toNavigationUnlessCurrent(current, toClaritySectionUrl(sectionPath, url), target.replace ?? false);
}
